import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import moment from 'moment'
import { jsPDF } from 'jspdf'
import { Workbook } from 'exceljs'
import { saveAs } from 'file-saver'
import i18n from '@/plugins/i18n'
import IMask from 'imask'
import 'devextreme/ui/color_box'

import TextBox from 'devextreme/ui/text_box'
import SelectBox from 'devextreme/ui/select_box'
import DropDownBox from 'devextreme/ui/drop_down_box'
import TagBox from 'devextreme/ui/tag_box'
import DateBox from 'devextreme/ui/date_box'
import NumberBox from 'devextreme/ui/number_box'
import CheckBox from 'devextreme/ui/check_box'
import { formatNumber } from 'devextreme/localization'
// import numberLocalization from 'devextreme/localization/number'
import { exportDataGrid as pdfExporter } from 'devextreme/pdf_exporter'
import { exportDataGrid as excelExporter } from 'devextreme/excel_exporter'
import DataSource from 'devextreme/data/data_source'
import ODataStore from 'devextreme/data/odata/store'

import { codeNameDisplayExpr } from './label'

import { useWebSiteStore } from '@/stores/web-site.store'
import { useRegistryStore } from '@/stores/registry.store'
import { usePortalStore } from '@/stores/portal.store'

import { useNotify } from '@/composables/notify'
import { useGetOrFetchAsync } from '@/composables/store'
import { useException } from '@/composables/exceptions'
import { isNullOrUndefined } from '@/lib/utils'
import router, { menuRoutesEnum } from '@/router'

const altPressed = ref(false)

const enterPressedTimestamp = ref(0)
const escapePressedTimestamp = ref(0)

export const focusedRow = ref(null)
export const masterGridFocusedRow = ref(null)
export const focusedGrid = ref(null)
export const isSaveAndClone = { value: false }
export const isSaveAndNew = ref(false)
export const formData = {}
export const lastSavedData = {}
export const lastSavedDocumentRowType = ref(null)
export const openEditingTab = ref(null)

let entityLock = null
let entityLockReadOnlyDisabled = false

const editingModeEnum = {
  NONE: 0,
  INSERT: 1,
  UPDATE: 2,
  DELETE: 3,
}

export const editingMode = ref(0)
export const isAdding = computed(() => editingMode.value === editingModeEnum.INSERT)

export const useGridRouter = () => {
  const route = useRoute()
  const router = useRouter()
  return { route, router }
}

export const useGridRef = () => {
  const gridRef = ref(null)
  const refreshGridAsync = () => new Promise((resolve, reject) => {
    gridRef.value.instance.refresh().done((res) => resolve(res)).fail((res) => reject(res))
  })
  const focusGrid = () => setFocusOnDatagrid(gridRef.value.instance)

  const onFocusedRowChangedDebounced = async(e, selectedDocument) => {
    selectedDocument.value = e.row?.data
  }

  const setCellValueByField = (field, value) => {
    const grid = gridRef.value?.instance

    const editingRow = grid?.getVisibleRows().find(row => row.isEditing)
    if (!editingRow) return

    const rowIndex = editingRow.rowIndex
    grid.cellValue(rowIndex, field, value)
  }

  const setFocusAfterFilterAsync = async(e, applyRouteFilterAsync) => {
    const filterApplied = await applyRouteFilterAsync()
    const dataSource = e.component.getDataSource()

    if (!filterApplied && e.component.option('isOptionsChanged') && !dataSource.isLoading()) {
      e.component.option('isOptionsChanged', false)
      const rows = e.component.getVisibleRows()

      e.component.option('focusedRowKey', null)
      if (rows.length > 0) { e.component.option('focusedRowKey', rows[0].key) }

      if (openEditingTab.value) {
        e.component.option('focusedRowIndex', 0)
        e.component.editRow(0)
      }
    }
  }

  const refreshEditFormLayout = (editColumns, value) => {
    gridRef.value?.instance?.option('editing.form.items', editColumns)
    setCellValueByField('vat_type_ref', value)
  }

  return { gridRef, refreshGridAsync, focusGrid, onFocusedRowChangedDebounced, setCellValueByField, setFocusAfterFilterAsync, refreshEditFormLayout }
}

export const useCurrencyColumnFormatter = (rowData, field, currencyId) => {
  const webSiteStore = useWebSiteStore()
  const registryStore = useRegistryStore()
  const defaultCurrencyCode = webSiteStore.getDefaultCurrency()?.code
  const currentCurrency = webSiteStore.currencies.find(i => i.id === currencyId)

  let format = { style: 'currency', currency: currentCurrency?.code || defaultCurrencyCode, useGrouping: true }

  if (field === 'price' || field === 'product_price' || field === 'price_vat') {
    format = useCurrencyOptions({currency: currentCurrency, precision: registryStore.companyData.default_display_price_precision})?.format
  }

  return rowData[field] && formatNumber(+rowData[field], format)
}

export const usePercentageOptions = (options = null) => {
  const opts = {...{ precision: 2, isEditor: false }, ...options}
  const format = { type: 'percent', precision: opts.precision }
  return { format: format, step: 0.01 }
}

export const currencyPrecision = (currency) => {
  const numberFormatUSD = new Intl.NumberFormat('en-US', { style: 'currency', currency })
  return numberFormatUSD.formatToParts(1)
    .find(part => part.type === 'fraction')
    ?.value.length ?? 0
}

export const useCurrencyOptions = (options = null) => {
  const webSiteStore = useWebSiteStore()
  const currency = options?.currency || webSiteStore.getDefaultCurrency()
  let format = { type: 'currency', currency: currency.code, precision: currencyPrecision(currency.code), useGrouping: true }
  if (options?.precision) {
    const currencyDigits = currencyPrecision(currency.code)
    const optionalDigits = Math.max(0, options.precision - currencyDigits)
    format = `${currency.symbol}#0${options.precision > 0 ? '.' : ''}${'0'.repeat(currencyDigits)}${'#'.repeat(optionalDigits)}`
  }
  return { format: format }
}

export const useMaxDiscountValueOptions = (documentRowType) => {
  const webSiteStore = useWebSiteStore()

  return { max: documentRowType !== webSiteStore.documentRowTypesEnum.EXPENSE ? 1.00 : null, min: 0 }
}

export const usePriceMinValueOptions = (documentRowType) => {
  const webSiteStore = useWebSiteStore()

  return { min: documentRowType !== webSiteStore.documentRowTypesEnum.EXPENSE ? 0 : null }
}

export const useQuantityMinValueOptions = (documentRowType) => {
  const webSiteStore = useWebSiteStore()

  return { min: [webSiteStore.documentRowTypesEnum.PRODUCT, webSiteStore.documentRowTypesEnum.LABOR, webSiteStore.documentRowTypesEnum.VEHICLE, webSiteStore.documentRowTypesEnum.EXPENSE].includes(documentRowType) ? 1 : 0 }
}

export const useDateConverter = (model, excludedFields = []) => {
  for (const prop in model) {
    if (!excludedFields.includes(prop) && model[prop] instanceof Date) {
      model[prop] = moment(model[prop]).format('YYYY-MM-DD')
    }
  }
  return model
}

export const useLookup = (options = null) => {
  const defaultOptions = {
    valueExpr: 'id',
    displayExpr: options.codeOnly ? 'code' : codeNameDisplayExpr,
    dataSource: options.dataSource || { store: options.store, sort: options.sort || 'code' },
  }

  return {...defaultOptions, ...options }
}

export const useDefaultOnRowClick = (e) => {
  const target = e.event?.target

  if (
    target?.closest('.dx-editor-cell') ||
    target?.closest('.dx-texteditor') ||
    target?.closest('.dx-filter-row')
  ) {
    return
  }

  e.component.keyboardListenerEnabled = true
  e.component.option('focusedRowKey', e.key)
  setFocusOnDatagrid(e.component)
}
export const useDefaultOnRowInserting = (e) => {
  useDateConverter(e.data)
  lastSavedData.value = {...e.data}
  lastSavedDocumentRowType.value = e.data?.document_row_type
}
export const useDefaultOnRowUpdating = (e) => {
  useDateConverter(e.newData)
  lastSavedData.value = {...e.oldData, ...e.newData}
  lastSavedDocumentRowType.value = lastSavedData.value?.document_row_type
}

export const useDefaultOnRowInserted = (e) => {
  e.component.option('focusedRowKey', e.key)

  // expand the created row if the masterDetail is enabled
  if (e.component.option('masterDetail').enabled) {
    e.component.expandRow(e.key)
  }
}

export const useDefaultOnInitNewRow = (e) => {
  e.component.keyboardListenerEnabled = false
  editingMode.value = editingModeEnum.INSERT
  const editingPopupTitleFn = e.component.option('editing.popup.titleFn')
  if (editingPopupTitleFn) {
    e.component.option('editing.popup.title', editingPopupTitleFn(e.data))
  }
  if (isSaveAndClone.value) {
    e.data = {...formData.value, ...lastSavedData.value}
    e.data.id = null
    formData.value = lastSavedData.value = null
    isSaveAndClone.value = false
  }
  if (isSaveAndNew.value) {
    if (lastSavedDocumentRowType.value !== null) {
      e.data.document_row_type = lastSavedDocumentRowType.value
    }
  }
}

export const useDefaultOnEditingStart = (e) => {
  e.component.keyboardListenerEnabled = false
  editingMode.value = editingModeEnum.UPDATE

  const editingPopupTitleFn = e.component.option('editing.popup.titleFn')
  if (editingPopupTitleFn) {
    e.component.option('editing.popup.title', editingPopupTitleFn(e.data))
  }

  const gridId = e.component.element().id
  const alreadyLocked = !isNullOrUndefined(e.component.option('alreadyLocked')) && e.component.option('alreadyLocked')
  const entityLockId = e.component.option('entityLockId')?.() ?? e.key
  if (entityLock && !gridId.endsWith('-row-catalog-import-grid-validation') && !gridId.endsWith('-layout-config-grid')) {
    if (!alreadyLocked) {
      entityLock.lockEntity(entityLockId)
      if (!isNullOrUndefined(e.component.option('alreadyLocked'))) {
        e.component.option('alreadyLocked', true)
      }
    }
  }

  if (entityLock?.isLocked(false, entityLockId) && !entityLockReadOnlyDisabled) {
    entityLock.isReadOnly.value = true
    return
  }
}

export const useDefaultOnEditCanceled = (e) => {
  const webSiteStore = useWebSiteStore()
  e.component.keyboardListenerEnabled = true
  editingMode.value = editingModeEnum.NONE
  useFocusOnDatagrid(e)
  webSiteStore.onGridEditCanceled(e)

  if (entityLock) {
    entityLock.releaseLock()
  }
}

export const useDefaultPager = {
  visible: false,
}

export const useDefaultPaging = {
  pageSize: 20,
  enabled: true,
}

export const useDefaultScrolling = {
  mode: 'virtual',
}

export const useDefaultFilterRow = {
  visible: true,
  applyFilter: 'auto',
  showAllText: '',
}

export const useDefaultSorting = {
  mode: 'multiple'
}

export const useDefaultHeaderFilter = {
  visible: true,
  allowSearch: true,
}

export const useDefaultColumnChooser = {
  enabled: true,
  mode: 'select',
  position: { my: 'right top', at: 'right bottom', of: '.dx-datagrid-column-chooser-button' },
}

export const useDefaultExport = (options) => {
  options = {
    fileName: 'export',
    isLandscape: false,
    formats: ['XLSX', 'PDF'],
    ...options
  }

  const exportOptions = {
    enabled: true,
    allowExportSelectedData: false,
    formats: options.formats,
  }

  const exportToPdf = async(e) => {
    // eslint-disable-next-line new-cap
    const doc = new jsPDF(options.isLandscape ? 'l' : 'p', 'mm', [210, 297]) // portrait A4

    await pdfExporter({
      jsPDFDocument: doc,
      component: e.component,
      indent: 5,
    })

    doc.save(`${options.fileName}.pdf`)
  }

  const exportToExcel = async(e) => {
    const workbook = new Workbook()
    const worksheet = workbook.addWorksheet(options.fileName)

    await excelExporter({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
      customizeCell: options.customizeCell,
    })
    const buffer = await workbook.xlsx.writeBuffer()
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${options.fileName}.${e.format.toLowerCase()}`)
  }

  const onExporting = async(e) => {
    e.cancel = true
    if (e.format === 'PDF') {
      await exportToPdf(e)
    } else {
      await exportToExcel(e)
    }
  }

  return { exportOptions, onExporting, exportToExcel, exportToPdf }
}

export const useDefaultErrorHandler = (e) => {
  const { t } = i18n.global

  const setValidationOnEditor = (editor, detail, label) => {
    editor.option({
      validationStatus: 'invalid',
      validationErrors: detail.errors.map(i => {
        return { message: t(i.code, {field: label, ...i.params}) || i.code || i }
      })
    })
  }

  let isManaged = false
  if (e.error?.httpStatus >= 400 && e.error?.httpStatus < 500) {
    if (e?.error?.errorDetails?.details && e?.error?.errorDetails.details.length > 0) {
      e.error.errorDetails.details.forEach(detail => {
        const searchRowIndex = editingMode.value === editingModeEnum.INSERT ? 0 : focusedRow?.value?.rowIndex

        // The get cell element uses the rowIndex and the column index to get the cell element from the GRID or from the "edit" POPUP
        // If you do not specify the current editing row index, the cell element will be retrieved from the GRID
        let editorInstance = null
        if (e.component?.getCellElement) {
          let cellElement = e.component.getCellElement(searchRowIndex, detail.target)
          if (!cellElement) {
            cellElement = e.component.getCellElement(searchRowIndex, detail.target + '_ref')
          }
          if (cellElement) {
            let editorElements = cellElement.getElementsByClassName('dx-texteditor')
            editorInstance = TextBox.getInstance(editorElements[0])
            if (!editorInstance) {
              editorInstance = SelectBox.getInstance(editorElements[0])
            }
            if (!editorInstance) {
              editorInstance = DropDownBox.getInstance(editorElements[0])
            }
            if (!editorInstance) {
              editorInstance = TagBox.getInstance(editorElements[0])
            }
            if (!editorInstance) {
              editorInstance = DateBox.getInstance(editorElements[0])
            }
            if (!editorInstance) {
              editorInstance = NumberBox.getInstance(editorElements[0])
            }
            if (!editorInstance) {
              editorElements = cellElement.getElementsByClassName('dx-checkbox')

              for (const item of editorElements) {
                const inputs = item.getElementsByTagName('input')
                if (inputs.length && inputs[0].value === 'null') {
                  editorInstance = CheckBox.getInstance(item)
                  break
                }
              }
            }
          }
        } else {
          editorInstance = e.component.getEditor(detail.target) ?? e.component?._editors?.[detail.target]
        }
        if (editorInstance) {
          isManaged = true
          const label = editorInstance.option('label')
          setValidationOnEditor(editorInstance, detail, label)
        }
      })
    }
  }
  if (!isManaged) {
    const { processError } = useException()
    console.error(e.error?.errorDetails || e.error)
    processError(e.error)
  }
  return isManaged
}

export const useGenerateDocumentWarning = () => {
  const { notifyWarning } = useNotify()
  const { t } = i18n.global

  const registryStore = useRegistryStore()
  const portalStore = usePortalStore()

  const warnings = ref([])

  const addWarning = (documentName, message) => {
    const existing = warnings.value.find(w => w.document === documentName)

    if (existing) {
      existing.messages.push(message)
    } else {
      warnings.value.push({
        document: documentName,
        messages: [message]
      })
    }
  }

  const checkPartialFields = async(generateDocumentType, docName, selectedRows, addDocumentWarning) => {
    if ([portalStore.documentGenerateEnum.IMMEDIATE_INVOICE, portalStore.documentGenerateEnum.DEFERRED_INVOICE, portalStore.documentGenerateEnum.CREDIT_NOTE].includes(generateDocumentType)) {
      const customers = await registryStore.getOrFetchCustomersAsync()
      const vehicles = await registryStore.getOrFetchVehiclesAsync()
      const customer = (selectedRows ?? customers).map(c => registryStore.customers.find(rc => rc.id === c.customer_id)).find(customer => customer?.is_partial || customer?.privacy_check === false)
      const exists_partial_vehicle = (selectedRows ?? vehicles).some(v => registryStore.vehicles.find(rv => rv.id === v.vehicle_id)?.is_partial)
      if (customer) {
        const subjectValidation = (customer.is_partial ? portalStore.subjectValidationTypesEnum.PARTIAL : portalStore.subjectValidationTypesEnum.NONE) | (customer.privacy_check === false ? portalStore.subjectValidationTypesEnum.PRIVACY : portalStore.subjectValidationTypesEnum.NONE)
        const routeData = router.resolve({ path: menuRoutesEnum.SUBJECTS, query: { code: customer.code, openEdit: true, subjectValidation, tab: customer.is_partial ? 0 : 1 } })
        const action = [
          `<a href="${routeData.href}" target="_blank" class="text-black">`,
          t('error.validation.partial_entity_in_document_action'),
          '<i class="q-icon mdi mdi-pencil q-ml-xs"></i>',
          '</a>',
        ].join('')
        const messages = [
          customer.is_partial && t('error.validation.partial_customer_in_document_to_complete'),
          customer.privacy_check === false && t('error.validation.privacy_check'),
        ].filter(Boolean).join('<br>')
        addDocumentWarning(docName, `${messages}<br>${action}`)
      }
      if (exists_partial_vehicle) {
        const vehicle_id = registryStore.vehicles.find(rv => rv.id === (selectedRows ?? vehicles)[0]?.vehicle_id)?.id
        const routeData = router.resolve({ path: menuRoutesEnum.VEHICLES, query: { code: vehicle_id, openEdit: true } })
        const action = [
          `<a href="${routeData.href}" target="_blank" class="text-black">`,
          t('error.validation.partial_entity_in_document_action'),
          '<i class="q-icon mdi mdi-pencil q-ml-xs"></i>',
          '</a>',
        ].join('')
        addDocumentWarning(docName, `${t('error.validation.partial_vehicle_in_document')}<br>${action}`)
      }
    }
  }

  const displayWarnings = (options) => {
    if (warnings.value.length > 0) {
      const message = `
      <strong style="font-size: 1.15rem;">${t('common.generationError')}</strong>
      <ul>
        ${warnings.value
          .map(w => `
            <li>
              <strong>${w.document}</strong>
              <ul>
                ${w.messages.map(m => `<li>${m}</li>`).join('')}
              </ul>
            </li>
          `)
          .join('')}
      </ul>`

      notifyWarning(message, {
        timeout: 0,
        actions: [{ icon: 'mdi-close', color: 'black', round: true, handler: () => {} }],
        ...options })
      warnings.value = []
      return true
    }
    return false
  }

  return { addWarning, displayWarnings, checkPartialFields }
}

export const gridLayoutConfigToolbarItem = {
  widget: 'dxButton',
  location: 'after',
  name: 'gridLayoutConfigButton',
  template: 'gridLayoutConfigTemplate',
}

export const useDefaultToolbar = (e, options) => {
  const defaultToolbar = {}

  const items = e.toolbarOptions.items
  const addBtnIndex = items.findIndex(i => i.name === 'addRowButton')

  if (addBtnIndex !== -1 && options?.addButtonOptions) {
    items[addBtnIndex].options = { ...items[addBtnIndex].options, ...options.addButtonOptions, hint: i18n.global.t('common.add') }
  }

  if (addBtnIndex !== -1 && !options?.addButtonOptions?.visible && options?.useDefaultAddButton !== true) {
    if (entityLock && e.component.option('editing.allowAdding')) {
      items.splice(addBtnIndex, 1)

      items.splice(addBtnIndex, 0, {
        widget: 'dxButton',
        location: 'after',
        name: 'addCustomRowButton',
        template: 'customToolbarAddButtonTemplate'
      })
    }
  }

  if (options.allowBatchDelete) {
    items.splice(addBtnIndex + 1, 0, {
      widget: 'dxButton',
      location: 'after',
      name: 'batchDeleteButton',
      template: 'customToolbarDeleteButtonTemplate'
    })
  }

  if (options.allowResetSettings) {
    items.splice(addBtnIndex + 1, 0, {
      widget: 'dxButton',
      location: 'after',
      name: 'resetSettingsButton',
      template: 'customToolbarResetSettingsButtonTemplate'
    })
  }

  return defaultToolbar
}

export const useDefaultEditing = (title, form, options = null) => {
  const { t } = i18n.global

  const defaultOptions = {
    width: '110%', height: 'auto',
    allowAdding: true,
    allowUpdating: true,
    allowDeleting: true,
  }
  const opts = {...defaultOptions, ...options}

  const editingOptions = {
    allowAdding: opts.allowAdding,
    allowUpdating: opts.allowUpdating,
    allowDeleting: opts.allowDeleting,
    useIcons: true,
    mode: opts?.mode || 'popup',
    form: form || {},
    popup: {
      title: title || t('common.edit'),
      titleFn: typeof title === 'function' ? title : null,
      showTitle: true,
      width: opts.width || 'auto',
      height: opts.height || 'auto',
      onShowing: (e) => {
        if (typeof opts.onInitializedCustom === 'function') {
          opts.onInitializedCustom(e, options?.hasClaim)
        }
      }
    },
  }

  if (!options?.hasClaim || options?.hasClaim()) {
    if (opts.isSaveAndNew || opts.isSaveAndClone) {
      if (opts.isSaveAndNew) {
        editingOptions.popup.toolbarItems = [
          { widget: 'dxButton', location: 'after', toolbar: 'bottom',
            options: {
              text: t('common.save'),
              onClick: (e) => {
                isSaveAndNew.value = isSaveAndClone.value = false
                focusedGrid.value.saveEditData()
                lastSavedData.value = null
              },
            }
          },
          {
            widget: 'dxButton', location: 'after', toolbar: 'bottom',
            options: {
              text: t('common.saveAndNew'),
              onClick: (e) => {
                isSaveAndNew.value = true
                isSaveAndClone.value = false
                focusedGrid.value.saveEditData()
                lastSavedData.value = null
              },
            }
          }
        ]
      }

      if (opts.isSaveAndClone) {
        editingOptions.popup.toolbarItems.push(
          {
            widget: 'dxButton', location: 'after', toolbar: 'bottom',
            options: {
              text: t('common.saveAndClone'),
              onClick: (e) => {
                isSaveAndNew.value = false
                isSaveAndClone.value = true
                focusedGrid.value.saveEditData()
              },
            }
          }
        )
      }

      editingOptions.popup.toolbarItems.push(
        {
          widget: 'dxButton', location: 'after', toolbar: 'bottom',
          options: {
            text: t('common.cancel'),
            onClick: () => {
              isSaveAndNew.value = false
              focusedGrid.value.cancelEditData()
              lastSavedData.value = null
            }
          }
        }
      )
    }
  }

  if (opts.entityLock) {
    entityLock = opts.entityLock
  }

  return editingOptions
}

export const useDefaultRowReordering = (reorderingApi, itemIdRef, gridRef, isReadOnly) => {
  const onReorder = async(e) => {
    if (isReadOnly && isReadOnly(e)) {
      return
    }

    const fromId = e.itemData?.id
    const toRow = e.component.getVisibleRows()[e.toIndex]
    const toId = toRow?.key

    if (fromId == null || toId == null || fromId === toId) {
      return
    }

    await reorderingApi(itemIdRef.value, {
      fromId,
      toId,
      isDropInsideItem: e.dropInsideItem
    })

    await e.component.refresh()
  }

  return { onReorder }
}

export const useDefaultStateStoring = (gridId) => {
  const webSiteStore = useWebSiteStore()
  const gridLoadState = (gridId) => {
    const gridState = webSiteStore.gridStates[gridId]
    if (gridState) {
      // clearing filters
      gridState.columns?.forEach((col) => {
        if (col.filterValue) {
          col.filterValue = null
        }
      })
      // clearing selected row
      gridState.selectedRowKeys = null
      return gridState
    }
    return gridState
  }

  const gridSaveState = (gridId, state) => {
    webSiteStore.setGridState(gridId, state)
  }

  return {
    enabled: true,
    type: 'custom',
    customLoad: () => gridLoadState(gridId),
    customSave: (state) => gridSaveState(gridId, state)
  }
}

export const masterGrid = ref(null)

export const gridAllowEditingAction = (grid, action) => {
  /// check if is function
  if (grid.option('editing')[action] instanceof Function) {
    return grid.option('editing')[action]({row: focusedRow.value})
  }
  return grid.option('editing')[action]
}

export const useDefaultKeyDownListener = () => {
  return (event) => {
    if (event.code === 'AltLeft' || event.code === 'AltRight') {
      altPressed.value = true
    } else if (altPressed.value && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault()
    } else if (event.code === 'F3' && !editingMode.value && focusedGrid.value?.keyboardListenerEnabled && !entityLock?.isLocked()) {
      const addRowFunction = focusedGrid.value.option('addRowFunction')
      if (typeof addRowFunction === 'function') {
        addRowFunction()
        event.preventDefault()
      } else if (gridAllowEditingAction(focusedGrid.value, 'allowAdding')) {
        focusedGrid.value.addRow()
        event.preventDefault()
      }
    } else if (event.code === 'F4' && !editingMode.value && focusedGrid.value?.keyboardListenerEnabled && gridAllowEditingAction(focusedGrid.value, 'allowUpdating') && !entityLock?.isLocked()) {
      focusedGrid.value.editRow(focusedRow.value.rowIndex)
      event.preventDefault()
    } else if (event.code === 'F6' && !editingMode.value && focusedGrid.value?.keyboardListenerEnabled && gridAllowEditingAction(focusedGrid.value, 'allowDeleting') && !entityLock?.isLocked()) {
      focusedGrid.value.deleteRow(focusedRow.value.rowIndex)
      editingMode.value = editingModeEnum.DELETE
      event.preventDefault()
    }
  }
}

export const useDefaultKeyUpListener = () => {
  return (event) => {
    if (event.code === 'AltLeft' || event.code === 'AltRight') {
      altPressed.value = false
    } else if ((editingMode.value && editingMode.value !== editingModeEnum.DELETE || focusedGrid.value?.option('saveFunctionEnabled')?.()) && event.key === 'Enter') {
      if ((Date.now() - enterPressedTimestamp.value) < 500) {
        const saveFunction = focusedGrid.value.option('saveFunction')
        if (typeof saveFunction === 'function') {
          saveFunction()
        } else {
          document.activeElement.blur()
          focusedGrid.value.getController('validating').validate(true).done(isValid => isValid && focusedGrid.value.saveEditData())
        }
      } else {
        enterPressedTimestamp.value = Date.now()
      }
      event.preventDefault()
      event.stopPropagation()
    } else if (editingMode.value === editingModeEnum.DELETE && event.key === 'Escape') {
      focusedGrid.value.cancelEditData()
      event.preventDefault()
      event.stopPropagation()
    } else if ((editingMode.value || focusedGrid.value?.option('cancelFunctionEnabled')?.()) && event.key === 'Escape') {
      if ((Date.now() - escapePressedTimestamp.value) < 500) {
        const cancelFunction = focusedGrid.value.option('cancelFunction')
        typeof cancelFunction === 'function' ? cancelFunction() : focusedGrid.value.cancelEditData()
      } else {
        escapePressedTimestamp.value = Date.now()
      }
      event.preventDefault()
      event.stopPropagation()
    }
    if (altPressed.value) {
      altPressed.value = false
      if (event.key === 'ArrowDown' && !editingMode.value) {
        const isMasterDetail = focusedGrid.value?.option('masterDetail')?.enabled
        const isSplittedGrid = focusedGrid.value?.option('splittedGrid')
        if (isMasterDetail) {
          const rowKey = focusedGrid.value.option('focusedRowKey')
          focusedGrid.value.expandRow(rowKey)
          focusedGrid.value.keyboardListenerEnabled = false // to avoid the default behavior of the grid while opening the detail
        }
        if (isSplittedGrid) {
          setFocusOnDatagrid(masterGrid.value.childGrid)
        }
        event.preventDefault()
      } else if (event.key === 'ArrowUp' && !editingMode.value) {
        const parentGrid = focusedGrid.value?.parentGrid
        const childGrid = focusedGrid.value
        const isMasterDetail = parentGrid?.option('masterDetail')?.enabled
        const isSplittedGrid = parentGrid?.option('splittedGrid')
        if (isMasterDetail) {
          const rowKey = parentGrid.option('focusedRowKey')
          parentGrid.collapseRow(rowKey)
          parentGrid.refresh()
        }
        if (isSplittedGrid) {
          childGrid.option('focusedRowKey', null)
          setFocusOnDatagrid(parentGrid)
        }
        event.preventDefault()
      }
    }
  }
}

export const useDefaultOnFocusedRowChanging = (e) => {
  if (altPressed.value) {
    e.cancel = true
  }
  if (!e.component.keyboardListenerEnabled) {
    e.cancel = true
  }
}

export const useDefaultOnOptionChanged = (e) => {
  if (e.name.includes('columns') && e.fullName.includes('filterValue')) {
    e.component.option('isOptionsChanged', true)
  }
}

export const useDefaultOnRowExpandingDatagrid = (e) => {
  if (e.component.option('masterDetail').enabled) {
    e.component.collapseAll(-1)
  }
}

export const useDefaultOnEditorPreparing = (e) => {
  useRandomInputName().setRandomInputName(e)
  if (e.editorName === 'dxNumberBox') {
    e.editorOptions.onKeyDown = function(ev) {
      if (ev.event.key === 'e' || ev.event.key === 'E') { ev.event.preventDefault() }
    }
  }

  if (entityLock?.isReadOnly.value && !entityLockReadOnlyDisabled) {
    e.editorOptions.readOnly = entityLock.isReadOnly.value
  }
}

let focusedRowChangedTimeout = null
export const useDefaultOnFocusedRowChanged = (e) => {
  focusedRow.value = e.row
  e.component.focusedRowKey = e.row?.key
  e.component.keyboardListenerEnabled = true

  if (e.component.option('splittedGrid') && typeof e.component.option('onFocusedRowChangedDebounced') === 'function') {
    if (focusedRowChangedTimeout) {
      clearTimeout(focusedRowChangedTimeout)
    }
    focusedRowChangedTimeout = setTimeout(() => {
      const handler = e.component.option('onFocusedRowChangedDebounced')
      handler(e)
    }, 500)
  }
}

export const useDefaultKeyHandler = () => {
  const keyDownListener = useDefaultKeyDownListener()
  const keyUpListener = useDefaultKeyUpListener()
  document.addEventListener('keydown', keyDownListener)
  document.addEventListener('keyup', keyUpListener)
}

export const useDefaultOnContentReady = (e) => {
  const { setFocusAfterFilterAsync } = useGridRef()
  const { applyRouteFilterAsync } = useRouterFilter()

  const grid = e.component
  if (grid) {
    if (masterGrid.value && focusedGrid.value && !grid.parentGrid) {
      grid.parentGrid = focusedGrid.value
      focusedGrid.value.childGrid = grid
    }
    if (grid.option('splittedGrid') || (grid.option('masterDetail')?.enabled && !grid.parentGrid)) {
      masterGrid.value = grid
    }

    if (!grid.firstLoad) {
      grid.firstLoad = true
      if (!grid.option('disabledFocusOnContentReady')) {
        setTimeout(() => {
          useFocusOnDatagrid(e)
        }, 50)
      }
    }

    grid.columnOption('command:edit', 'visibleIndex', -1)
    grid.columnOption('command:edit', 'fixed', 'true')
    grid.columnOption('command:edit', 'fixed-position', 'left')

    const routerFilter = grid.option('routerFilter')
    if (routerFilter) {
      setFocusAfterFilterAsync(e, () => applyRouteFilterAsync(grid, routerFilter))
    }
  }
}

export const useRouterFilter = () => {
  const applyRouteFilterAsync = async(gridRef, routerFilter) => {
    if (!gridRef) return false
    if (!routerFilter?.getRoute) return false

    const query = routerFilter?.getRoute()?.query
    if (!query || Object.keys(query).length === 0) return false

    let filterApplied = false

    routerFilter.fields.forEach(obj => {
      Object.entries(obj).forEach(([key, column]) => {
        if (!column) return

        let filterValue = query[key]
        if (filterValue == null || filterValue === '') return

        if (key === 'series' || key === 'id') {
          filterValue = filterValue.toString()
        }

        if (key === 'supplier_brand_ref') {
          filterValue = Number(filterValue)
        }

        gridRef.columnOption(column, 'selectedFilterOperation', '=')
        gridRef.columnOption(column, 'filterValue', filterValue)

        filterApplied = true
      })
    })

    if (filterApplied) {
      await routerFilter.router.replace({ query: undefined })
    }

    if (query['tab']) {
      openEditingTab.value = query['tab']
    }

    return filterApplied
  }

  return { applyRouteFilterAsync }
}

export const useFocusOnDatagrid = (e) => setFocusOnDatagrid(e.component)

const setFocusOnDatagrid = (grid) => {
  focusedGrid.value = grid
  if (grid) {
    if (grid.option('focusedRowIndex') < 0) {
      grid.option('focusedRowIndex', 0)
    }
    grid.focus()
    grid.keyboardListenerEnabled = true
  }
}

export const useDefaultAddressEditing = (addressType, colSpan, colCount, editorOptions, flat = false) => {
  const { t } = i18n.global
  if (flat) {
    return [
        { dataField: `${addressType}.country`, label: { text: t('common.country') }, editorOptions: editorOptions, colSpan: colSpan },
        { dataField: `${addressType}.zip_code`, label: { text: t('common.zipCode') }, editorOptions: editorOptions, colSpan: colSpan },
        { dataField: `${addressType}.city`, label: { text: t('common.cityAddress') }, editorOptions: editorOptions, colSpan: colSpan },
        { dataField: `${addressType}.city_text`, label: { text: t('common.cityAddress') }, visible: false, editorOptions: editorOptions, colSpan: colSpan },
        { dataField: `${addressType}.state`, label: { text: t('common.state') }, editorOptions: { showClearButton: true, ...editorOptions }, colSpan: colSpan },
        { dataField: `${addressType}.state_text`, label: { text: t('common.state') }, visible: false, editorOptions: editorOptions, colSpan: colSpan },
        { dataField: `${addressType}.street`, label: { text: t('common.street') }, editorOptions: editorOptions, colSpan: colSpan * 2 },
    ]
  }

  return {
    itemType: 'group',
    caption: t('common.address'),
    colSpan: colSpan,
    colCount: colCount,
    items: [
        { dataField: `${addressType}.country`, label: { text: t('common.country') }, editorOptions: editorOptions },
        { dataField: `${addressType}.zip_code`, label: { text: t('common.zipCode') }, editorOptions: editorOptions },
        { dataField: `${addressType}.city`, label: { text: t('common.cityAddress') }, editorOptions: editorOptions },
        { dataField: `${addressType}.city_text`, label: { text: t('common.cityAddress') }, visible: false, editorOptions: editorOptions },
        { dataField: `${addressType}.state`, label: { text: t('common.state') }, editorOptions: { showClearButton: true, ...editorOptions } },
        { dataField: `${addressType}.state_text`, label: { text: t('common.state') }, visible: false, editorOptions: editorOptions },
        { dataField: `${addressType}.street`, label: { text: t('common.street') }, colSpan: 2, editorOptions: editorOptions },
    ]
  }
}

export const onDefaultSaved = async(e) => {
  editingMode.value = editingModeEnum.NONE
  if (isSaveAndNew.value || isSaveAndClone.value) {
    focusedGrid.value.addRow()
  } else {
    useFocusOnDatagrid(e)
  }

  if (entityLock) {
    entityLock.releaseLock()
  }
}

export const onDefaultRowRemoved = (e) => {
  editingMode.value = editingModeEnum.NONE
  useFocusOnDatagrid(e)
}

const withFocus = (focusGrid, handler) => (e) => {
  if (focusGrid) focusGrid()
  handler?.(e)
}

export const useDefaultGridSelection = () => {
  return { mode: 'multiple', showCheckBoxesMode: 'always', selectAllMode: 'page' }
}

export const toolbarClearFilterIcon = (grid, options = {}) => {
  return {
    widget: 'dxButton',
    location: 'after',
    options: {
      icon: 'mdi mdi-filter-off',
      hint: i18n.global.t('common.clearFilter'),
      disabled: false,
      onClick: (e) => {
        if (options.onClearFilter) {
          options.onClearFilter({ component: grid })
        }
        const currentFilter = grid.instance().getDataSource().filter()
        grid.instance().clearFilter()
        grid.instance().getDataSource().filter(currentFilter)
        grid.instance().getDataSource().load()
      },
    }
  }
}

export const toolbarRefreshIcon = (grid) => {
  return {
    widget: 'dxButton',
    location: 'after',
    options: {
      icon: 'mdi mdi-refresh',
      hint: i18n.global.t('common.reload'),
      disabled: false,
      onClick: (e) => {
        grid.refresh()
      },
    }
  }
}

export const useDefaultOnInitialized = (e, options = null) => {
  const grid = e.component
  const focusGrid = options?.focusGrid
  const enableGrouping = options?.enableGrouping
  grid.on('initNewRow', withFocus(focusGrid, useDefaultOnInitNewRow))
  grid.on('editingStart', withFocus(focusGrid, useDefaultOnEditingStart))
  grid.on('rowClick', (e) => useDefaultOnRowClick(e))
  grid.on('rowInserting', (e) => useDefaultOnRowInserting(e))
  grid.on('rowInserted', (e) => useDefaultOnRowInserted(e))
  grid.on('rowUpdating', (e) => useDefaultOnRowUpdating(e))
  grid.on('rowRemoved', (e) => onDefaultRowRemoved(e))
  grid.on('editCanceled', (e) => useDefaultOnEditCanceled(e))
  grid.on('contentReady', (e) => useDefaultOnContentReady(e))
  grid.on('focusedRowChanged', (e) => useDefaultOnFocusedRowChanged(e))
  grid.on('focusedRowChanging', (e) => useDefaultOnFocusedRowChanging(e))
  grid.on('optionChanged', (e) => useDefaultOnOptionChanged(e))
  grid.on('rowCollapsed', (e) => useFocusOnDatagrid(e))
  grid.on('rowExpanding', (e) => useDefaultOnRowExpandingDatagrid(e))
  grid.on('saved', (e) => onDefaultSaved(e))
  grid.on('editorPreparing', (e) => useDefaultOnEditorPreparing(e))
  // grid.on('toolbarPreparing', (e) => useDefaultOnToolbarPreparing(e))
  grid.on('keyDown', (e) => {
    if (e.event.key === 'ArrowLeft' || e.event.key === 'ArrowRight' || e.event.key === 'Tab') {
      e.handled = true
      e.event.preventDefault()
    }
  })

  const toolbarItems = []

  if (options?.gridTitle) {
    toolbarItems.push({
      location: 'before',
      template: () => `<span class="text-weight-bold">${options.gridTitle}</span>`,
    })
  }

  toolbarItems.push(
    { name: 'addRowButton' },
    { name: 'exportButton' },
    { name: 'columnChooserButton' },
  )

  // Clear filter button
  if (!options?.hideClearFilterButton) {
    toolbarItems.push(toolbarClearFilterIcon(grid, options))
  }

  // Refresh button
  if (!options?.hideRefreshButton) {
    toolbarItems.push(toolbarRefreshIcon(grid))
  }

  if (!enableGrouping) {
    grid.option('toolbar', { items: toolbarItems })
  }
}

export const useDefaultGridOptions = (options = null) => {
  if (options?.columns) {
    options.columns = options?.columns?.map(col =>
      col.type === 'buttons' ? { ...col, showInColumnChooser: false } : col
    )
  }
  if (options?.entityLock) {
    entityLock = options.entityLock
  }
  entityLockReadOnlyDisabled = !!options?.entityLockReadOnlyDisabled

  return {...{
    // columns
    // https://js.devexpress.com/Demos/WidgetsGallery/Demo/DataGrid/ColumnCustomization/Vue/Light/
    allowColumnReordering: true,
    allowColumnResizing: true,
    groupPanel: options?.groupPanel ?? { visible: false },
    grouping: options?.grouping ?? { contextMenuEnabled: false },
    showColumnLines: true,
    columnChooser: useDefaultColumnChooser,
    showBorders: true,
    remoteOperations: true,
    columnAutoWidth: false,
    columnResizingMode: 'widget',
    syncLookupFilterValues: false,
    pager: useDefaultPager,
    paging: useDefaultPaging,
    stateStoring: (options?.saveStateId || options?.id) && useDefaultStateStoring(options?.saveStateId || options?.id),
    repaintChangesOnly: options?.repaintChangesOnly || true,
    columnFixing: {
      enabled: true,
    },
    height: (options?.splittedGrid || options?.splittedGridDetail) ? '100%' : '90vh',
    width: '100%',
    columnWidth: options?.columnWidth || 150,
    loadPanel: { enabled: !options?.splittedGridDetail },

    // sorting & filtering
    // https://js.devexpress.com/Demos/WidgetsGallery/Demo/DataGrid/MultipleSorting/Vue/Light/
    sorting: options?.sorting || useDefaultSorting,
    filterRow: options?.filterRow || useDefaultFilterRow,
    scrolling: { ...(options?.scrolling || useDefaultScrolling), showScrollbar: 'onHover' },

    // focused row
    // https://js.devexpress.com/Demos/WidgetsGallery/Demo/DataGrid/FocusedRow/Vue/Light/
    focusedRowEnabled: true,
    autoNavigateToFocusedRow: false,

    // show validation errors
    // https://supportcenter.devexpress.com/ticket/details/t933572/datagrid-how-to-display-validation-errors-that-came-from-the-server
    errorRowEnabled: false,

    isOptionsChanged: false, // Custom option
    onDataErrorOccurred: useDefaultErrorHandler,
    onInitialized: (e) => useDefaultOnInitialized(e, options),
  }, ...options}
}

export const useODataStore = (url, options = null) => {
  const store = new ODataStore({
    url: url,
    beforeSend: (e) => {
      const webSiteStore = useWebSiteStore()

      if (options?.customParamsFn) {
        const params = options.customParamsFn()
        if (params) {
          e.params = {...e.params, ...params}
        }
      }
      e.headers = {
        ...e.headers,
        'Authorization': 'Bearer ' + webSiteStore.accessToken,
        'Accept-Language': i18n.global.locale.value,
        'X-User-Data-Filter': webSiteStore.userDataFilter || false,
        'X-OData': true
      }

      if (e.params?.$filter) {
        const match = e.params.$filter.match(/\bid eq (\w+)/)
        if (!match || isNaN(match[1])) {
          e.params.$filter = e.params.$filter.replace(/\bid eq \w+/, '1 eq 1')
        }
        if (e.params.$filter === '') {
          delete e.params.$filter
        }
      }

      if (e.method === 'PUT' || e.method === 'PATCH' || e.method === 'DELETE') {
        const id = e.url.match(/\((-?\d+)\)/)[1]
        e.url = url + id + '/'
      } else {
        e.url = url
      }
    },

    withCredentials: true,
    deserializeDates: options?.deserializeDates ?? false,
    key: options?.key || 'id',
    keyType: 'Int32',
    version: 4
  })

  if (options?.storeByKey) {
    store.byKey = (key) => {
      return options.storeByKey(key, store)
    }
  } else {
    store.byKey = (key) => {
      if (!key || key === '') {
        return store.load()
      }
      if (typeof key === 'string') {
        const searchExpr = options?.searchExpr || ['code']
        const filter = searchExpr.map((expr) => {
          return [expr, 'contains', key]
        })
        return store.load({ filter: filter })
      }
      return store.load({ filter: ['id', '=', key] })
    }
  }

  return store
}

export const useODataSource = (url, options = null) => {
  const store = useODataStore(url, options)
  const dataSourceOptions = {
    store: store,
    pageSize: 20,
  }

  if (!options?.avoidDuplicateDatasourceIds) {
    dataSourceOptions.postProcess = (data) => {
      // DevExtreme's DataSource accumulates all previously loaded pages in its internal buffer
      // and passes the entire buffer to postProcess on every load (by design with virtual/infinite scrolling).
      // This causes duplicate keys and triggers E1040 ("key value is not unique").
      // Deduplication here is the recommended workaround.
      // See: https://supportcenter.devexpress.com/ticket/details/t1148635
      const ids = new Set()
      return data.filter(item => {
        const key = item?.id ?? item?.[options?.key]
        return key == null ? true : ids.has(key) ? false : !!ids.add(key)
      })
    }
  }

  return new DataSource({ ...dataSourceOptions, ...options })
}

export const useMask = () => {
  return {
    mobileMask: '(####) 000 00 00 0099',
    mobileMaskRules: { '0': /[0-9]/ },
    phoneMask: '#### 0099 00000099',
    dateDisplayFormat: 'dd/MM/yyyy',
    dateSerializationFormat: 'yyyy-MM-dd',
    percentageMask: '#0.##%',
    dateSimpleFormat: 'YYYY-MM-DD',
  }
}

export const usePhoneMask = (element, options = {}) => {
  if (!element) {
    return null
  }

  const input = element.querySelector('input')
  if (!input) {
    return null
  }

  const masks = [
    '000 00 00 000',
    '+0 000 00 00 000',
    '+00 000 00 00 000',
    '+000 000 00 00 000',
    '0000 000 00 00 000',
  ]

  const defaultOptions = {
    lazy: false,
    overwrite: false,
    placeholderChar: '_',
    dispatch: (_, dynamicMasked) => {
      const isOverwrite = Math.abs(input.value.length - input.selectionStart) > 0
      const hasPrefix = input.value.includes('+')
      let len = input.value.replace(/\D/g, '').length
      if (isOverwrite) {
        len--
      }

      let index = 0
      if (len <= 10 && !hasPrefix) {
        index = 0
      } else if (len <= 11) {
        index = 1
      } else if (len <= 12) {
        index = 2
      } else if (len <= 13) {
        index = 3
      } else {
        index = 4
      }

      return dynamicMasked.compiledMasks.find(m => m.mask === masks[index])
    },
    mask: masks.map(m => ({ mask: m })),
  }

  return IMask(input, { ...defaultOptions, ...options })
}

export const useCodeFieldEditorOptions = (options = null) => ({...{ maxLength: 6 }, ...options})

export const useZipCode = async(newData, value, addressType) => {
  const webSiteStore = useWebSiteStore()
  if (!newData[addressType]) {
    newData[addressType] = {}
  }
  if (!newData[addressType]?.city) {
    const cities = ref([])
    const filteredCities = await useGetOrFetchAsync(cities, () => webSiteStore.api.getCitiesAsync(), false)
    const cityByZip = filteredCities.filter(city => city.zip_code === value)
    if (cityByZip.length > 0) {
      newData[addressType].city = cityByZip[0].id
    }
  }
  newData[addressType].zip_code = value
}

export const useUpdateMasterDetail = (options = null) => {
  const repaintRow = async(gridRef, apiMethod, id, rowKey) => {
    if (id) {
      let data = null
      await apiMethod(id)
      .then(res => {
        const index = gridRef.value.instance.getRowIndexByKey(rowKey)
        const row = gridRef.value.instance.getVisibleRows()[index].data
        Object.assign(row, res.data)
        data = res.data
        gridRef.value.instance.repaintRows([index])
      })
      return data
    }
  }

  return { repaintRow }
}

export const useDefaultDialogTitle = () => {
  const { t } = i18n.global

  const defaultDialogTitle = (item, dialogName) => {
    const title = `${defaultActionTitle(item)} ${dialogName}`
    if (!item?.id) {
      return title
    }
    const subTitle = defaultItemTitle(item)
    return subTitle ? `${title} - ${subTitle}` : title
  }

  const defaultActionTitle = (item) => {
    return `${!item?.id ? t('common.add') : t('common.edit')}`
  }

  const defaultItemTitle = (item) => {
    let title = ''
    if (!item) {
      return title
    }
    if (item.name) {
      title += ` ${item.name}`
    }
    return title
  }

  return { defaultDialogTitle, defaultActionTitle, defaultItemTitle }
}

export const useRandomInputName = () => {
  const setRandomInputName = (e) => {
    if (e.parentType === 'dataRow' && ['dxTextBox', 'dxTextArea', 'dxNumberBox', 'dxDateBox'].includes(e.editorName)) {
      const randomAlias = `f_${Math.random().toString(36).slice(2)}`
      e.editorOptions.inputAttr = { name: randomAlias, autocomplete: 'off' }
    }
  }

  return { setRandomInputName }
}

export const useMultiSelectFilter = () => {
  const multiSelectFilterEditorRefs = ref({})
  const multiSelectFilterRefs = ref({})
  const currentFilters = ref({})
  const currentSelections = ref({})

  const getSelectedValues = (selected) => {
    return selected.map(item => item !== null && typeof item === 'object' ? item.id : item)
  }

  const buildFilterQuery = (selected, dataField) => {
    if (!selected || selected.length === 0) {
      return null
    }

    const values = getSelectedValues(selected)
    return values.map(id => [dataField, '=', id])
                  .reduce((acc, curr, index) => {
                    if (index === 0) {
                      return curr
                    }
                    return [acc, 'or', curr]
                  })
  }

  const applyFilter = (selected, dataField, gridRef, mainFilter) => {
    const grid = gridRef?.instance
    if (!grid) {
      return
    }

    if (!Array.isArray(selected) || selected.length === 0) {
      delete currentFilters.value[dataField]
      delete currentSelections.value[dataField]
    } else {
      currentFilters.value[dataField] = buildFilterQuery(selected, dataField)
      currentSelections.value[dataField] = getSelectedValues(selected)
    }

    const selectedValues = currentSelections.value[dataField] || []
    multiSelectFilterEditorRefs.value[dataField]?.option('value', selectedValues)
    multiSelectFilterRefs.value[dataField]?.setSelectedItemKeys(selectedValues)

    const filters = Object.values(currentFilters.value)
    let combinedFilter = null

    if (filters.length > 0) {
      combinedFilter = filters.reduce((acc, curr, index) => {
        if (index === 0) {
          return curr
        }
        return [acc, 'and', curr]
      })
    }

    let finalFilter

    if (combinedFilter) {
      if (mainFilter) {
        finalFilter = [mainFilter, 'and', combinedFilter]
      } else {
        finalFilter = combinedFilter
      }
    } else {
      finalFilter = mainFilter
    }

    if (finalFilter) {
      grid.filter(finalFilter)
    } else {
      grid.clearFilter()
    }
    grid.refresh()
  }

  const setMultiSelectFilterRef = (field, el) => {
    if (el) {
      multiSelectFilterRefs.value[field] = el
      el.setSelectedItemKeys(currentSelections.value[field] || [])
    }
  }

  const setMultiSelectFilterEditorRef = (field, editor) => {
    if (editor) {
      multiSelectFilterEditorRefs.value[field] = editor
      editor.option('value', currentSelections.value[field] || [])
    }
  }

  const clearMultiSelectFilter = (gridRef = null) => {
    currentFilters.value = {}
    currentSelections.value = {}
    Object.values(multiSelectFilterEditorRefs.value).forEach(editor => { editor?.option('value', []) })
    Object.values(multiSelectFilterRefs.value).forEach(ref => { ref?.clearFilter() })
    if (gridRef?.instance) {
      gridRef?.instance.clearFilter()
      gridRef?.instance.refresh()
    }
  }

  return { applyFilter, buildFilterQuery, setMultiSelectFilterEditorRef, setMultiSelectFilterRef, clearMultiSelectFilter }
}

export const useMultiSelectEditorOptions = (options) => {
  const { t } = i18n.global

  return {...{
    valueExpr: 'id',
    displayExpr: 'name',
    contentTemplate: 'multiSelectFilterRef',
    dropDownOptions: { width: 300 },
    placeholder: t('common.select'),
  }, ...options }
}
